import React from 'react';
import { createSelector } from 'reselect';
import { createScopeFactory, createModel, createDependency } from '@agilepoint/restate-core';
import { IAgilePointConfiguration, isProduction, IUserDetails } from '@agilepoint/wat';
import { useScope, useSelectors, ScopeRegistrar } from '@agilepoint/react-restate';
import { combineUrl, deepMerge, memoizeFunction } from '@agilepoint/utils-common';
import { IApplicationConfig, IHelpLinks } from '../../../types';
import { appContextDependency } from '../../../dependencies';
import { CoreApiClient } from '../clients';

const coreApiClientDependency = createDependency<CoreApiClient>(Symbol('CoreApiClient'));

const configFilePath = combineUrl(process.env.PUBLIC_URL, `config.json?${new Date()}`);

export function getConfig() {
    return fetch(configFilePath).then((r) => r.json());
}

export function createConfigScopeBuilder(initialState: Partial<IApplicationConfig>) {
    const modelBuilder = createModel({
        initialState: initialState as IApplicationConfig
    }, slice => {

        const selectAppConfig = createSelector(slice, config => config);
        const selectBasePathURL = createSelector(slice, config => config.basePath);
        const selectAgilePointConfig = createSelector(selectAppConfig, config => config.agilepoint);
        const selectMobileAppAcceleratorConfig = createSelector(selectAppConfig, config => config.mobileAppAccelerator);

        const selectCurrentUserDetails = createSelector(selectAgilePointConfig, agp => agp.userDetails);
        const selectCulture = createSelector(selectAgilePointConfig, selectCurrentUserDetails, (agp, userDetails) => agp.userLocale || userDetails.locale || agp.culture);

        const selectHelpBaseUrl = createSelector(selectAppConfig, config => config.mobileAppAccelerator.helpBaseUrl);

        const selectHelpLinks = createSelector(selectAppConfig, config => config.mobileAppAccelerator.helpLinks);
        const selectHelpLinkIdByType = createSelector(selectHelpLinks, (helpLinks) => memoizeFunction((type: keyof IHelpLinks) => helpLinks[type]));

        const selectAPIEndPoint = createSelector(slice, config => config.apiEndPoint);
        const isUserTypeAdmin = createSelector(selectAgilePointConfig, config => (config.userType === 'sysadmin'));

        return {
            isProduction: createSelector(selectAppConfig, config => isProduction(config.environment)),
            selectAgilePointConfig,
            selectMobileAppAcceleratorConfig,
            selectCurrentUserDetails,
            selectCulture,
            selectHelpBaseUrl,
            selectHelpLinkIdByType,
            selectBasePathURL,
            selectAPIEndPoint,
            isUserTypeAdmin
        };
    });

    const actionCreatorsFactory = modelBuilder.registerActions(selectors => ({
        loadConfig(state, config: Partial<IApplicationConfig>) {
            return { ...state, ...config };
        }
    }), (context, actions) => ({
        async initAsync() {
            const { configuration: { agilepoint } } = appContextDependency.from(context.iocContainer);
            const coreApiClient = coreApiClientDependency.from(context.iocContainer);
            const userDetails = await coreApiClient.getLoggedUserDetails(agilepoint.userDetails.userName);
            const config = await getConfig();
            const userDetailsConfig = getUserDetailsConfig(userDetails);
            actions.loadConfig({
                agilepoint: deepMerge({ ...agilepoint }, {
                    userDetails: userDetailsConfig
                } as IAgilePointConfiguration,
                    { userType: agilepoint.userType || '' }),
                mobileAppAccelerator: config.mobileAppAccelerator
            } as Partial<IApplicationConfig>);
        }
    }));

    return createScopeFactory({
        actionCreatorsFactory,
        modelFactory: modelBuilder.modelFactory,
        onBuildIocContainer: (builder) => {
            const { client } = appContextDependency.from(builder);
            coreApiClientDependency.inject(builder, new CoreApiClient(client));
        }
    });
}

const getUserDetailsConfig = memoizeFunction((userDetails: any): IUserDetails => ({
    fullName: userDetails?.displayName || '',
    userName: userDetails?.userName || '',
    locale: userDetails?.locale || '',
    eMailAddress: userDetails?.email || '',
    userOrgInfo: userDetails?.profileImage || '',
    ...userDetails
}));

export type ConfigScopeType = ReturnType<typeof createConfigScopeBuilder>;

const scopeId = 'config';

export const useConfigScope = (scopeName: string = scopeId) => useScope<ConfigScopeType>(scopeName);

export const useConfigScopeSelectors = (scopeName: string = scopeId) => useSelectors<ConfigScopeType>(scopeName);

export const useConfigScopeActions = (scopeName: string = scopeId) => useConfigScope(scopeName).actions;

export const ConfigScopeRegistrar = ({ children, name = scopeId }: React.PropsWithChildren<{ name?: string }>) => (
    <ScopeRegistrar scopeName={name} scopeCreator={createConfigScopeBuilder({})}>{children} </ScopeRegistrar>
);
